import { Component, input, computed, inject, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import {
  compileTemplate,
  sanitizeHtml,
  TEMPLATE_RENDERER_CONFIG,
} from '../template-renderer.config';

/**
 * @class TemplateRendererComponent
 * @description A highly secure, reactive component that takes raw AI-generated Handlebars/HTML templates,
 * compiles them with local data, sanitizes the output via DOMPurify to strip XSS vectors,
 * and binds it natively to the Angular DOM.
 */
@Component({
  selector: 'rapider-template-renderer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './template-renderer.component.html',
})
export class TemplateRendererComponent {
  // Use dynamic HostBindings to allow height to flow naturally without collapsing
  @HostBinding('class.block') readonly block = true;
  @HostBinding('class.w-full') readonly wFull = true;

  /** Angular's DomSanitizer is required to tell Angular to trust our pre-sanitized string. */
  private readonly sanitizer = inject(DomSanitizer);

  public readonly config = TEMPLATE_RENDERER_CONFIG;

  /** * Signal Input: The raw Handlebars string (HTML + Tailwind + Handlebars tags). */
  public template = input<string>('');

  /** * Signal Input: The JSON data state to be interpolated into the template. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public data = input<Record<string, any>>({});

  /** * Signal Input: Target view mode used to automatically strip media queries for simulations. */
  public simulateViewMode = input<'desktop' | 'tablet' | 'mobile'>('desktop');

  /**
   * Computed Signal: Reactively processes the template and data whenever either changes.
   * Execution Flow: 1. Compile Handlebars -> 2. Sanitize via DOMPurify -> 3. Bypass Angular Security.
   */
  public safeRenderedHtml = computed<SafeHtml>(() => {
    const rawTemplate = this.template();
    const stateData = this.data();
    const viewMode = this.simulateViewMode();

    // 1. Compile the Handlebars template with the provided state and target view mode
    const compiledHtml = compileTemplate(rawTemplate, stateData, viewMode);

    // 2. Sanitize the output to strip any potentially hallucinated malicious code
    const cleansedHtml = sanitizeHtml(compiledHtml);

    // 3. Mark as safe so Angular's [innerHTML] directive doesn't strip safe attributes
    return this.sanitizer.bypassSecurityTrustHtml(cleansedHtml);
  });
}
